import { RefObject, useEffect, useMemo, useRef } from 'react';

import {
  attrToPageTitle,
  EventAttributes,
  getAnalyticsGlobal,
  raiseEvent,
  useAutoCapturing,
  useSetAnalyticsGlobal,
} from '@cast/analytics';

type PageAnalyticsProps = {
  title: string;
  isMetaLoading?: boolean;
  meta?: EventAttributes;
  rootRef?: RefObject<HTMLDivElement>;
};

export type PageAnalyticsPropsUnion = PageAnalyticsProps | string | false;

type UsePageAnalyticsProps = {
  rootRef?: RefObject<HTMLDivElement | null | undefined>;
  analytics?: PageAnalyticsPropsUnion;
  enabled?: boolean;
};

export const usePageAnalytics = ({
  rootRef,
  analytics,
  enabled = true,
}: UsePageAnalyticsProps) => {
  const pageVisitEmitted = useRef<boolean>(undefined);

  const analyticsOptions: undefined | PageAnalyticsProps = useMemo(() => {
    if (!analytics || !enabled) {
      return;
    }
    if (typeof analytics === 'string') {
      return { title: analytics };
    }
    return analytics;
  }, [analytics, enabled]);

  useEffect(() => {
    if (pageVisitEmitted.current || !analyticsOptions) {
      return;
    }
    const waitForMeta = analyticsOptions.isMetaLoading;

    if (waitForMeta) {
      return;
    }

    const page = attrToPageTitle(analyticsOptions.title);
    const feature = getAnalyticsGlobal('feature');
    const title = `Visited page: ${feature ? `${feature} > ` : ''}${page}`;

    raiseEvent({
      title,
      type: 'pageVisit',
      page,
      ...analyticsOptions?.meta,
    });
    pageVisitEmitted.current = true;
  }, [analyticsOptions]);

  useSetAnalyticsGlobal('page', analyticsOptions?.title, {
    enabled,
  });

  useAutoCapturing({
    rootRef,
    enabled,
  });

  return analyticsOptions;
};
